import { useState, useEffect, useContext, useRef } from "react";
import { useForm } from "react-hook-form";
import { Box, Grid } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import axios from "axios";
import { LocaleContext } from "../../../routing/LangRouter";
import useDateChange from "../../../hooks/useDateChange.js";
import useDebouncedInputChange from "../../../hooks/useDebouncedInputChange .js";
import axiosInstance from "../../../api/axios.js";

//import constants
import { ItineraryContext } from "../../../constants/layouts/itinerary-layout";
import { IS_MOBILE } from "../../../constants/breakpoints.js";
import BASE_URL from "../../../constants/baseUrls.js";
import { CABIN_TYPE } from "../../../constants/cabinType.js";

//import general components
import { BaseButton } from "../../../components/common/base-button/base-button";
import RangeDatePicker from "../../../components/common/range-date-picker/range-date-picker.jsx";
import PersonCount from "../../../components/common/person-count/person-count";
import AutoCompleteComponent from "../../common/base-select/autocomplete-component.js";
import BaseCheckbox from "../../common/base-checkbox/base-checkbox.jsx";
import BaseTextField from "../../common/base-text-field/base-text-field.jsx";

//import images and icons
import Icon from "../../common/base-icon/icon.jsx";
import { ArrowSwapHorizontal, CloseCircle, Location, SearchNormal } from "iconsax-react";

//import styles
import "./search-form.scss";

export default function SearchForm() {
    //general variables
    const { control } = useForm();
    const navigate = useNavigate();
    const { locale } = useContext(LocaleContext);
    const { t } = useTranslation();
    const { routes, setRoutes } = useContext(ItineraryContext);
    const accessToken = localStorage.getItem("accessToken");

    const [showAutocomplete, setShowAutocomplete] = useState({ departure: IS_MOBILE ? false : true, destination: IS_MOBILE ? false : true });
    const handleShowAutocomplete = name => {
        setShowAutocomplete({ [name]: true });
    };

    //user favorites request
    const [userAnswers, setUserAnswers] = useState(null);
    const getUserAnswers = async () => {
        const response = await axiosInstance.get(`/account/user/favorites/`);
        setUserAnswers(response.data);
    };
    useEffect(() => {
        if (accessToken) getUserAnswers();
    }, []);

    //checking user answers for skip wizard
    const [answerFlag, setAnswerFlag] = useState(false);
    useEffect(() => {
        if (userAnswers) {
            Object.keys(userAnswers).forEach(answerKey => {
                if (userAnswers[answerKey] === null) {
                    setAnswerFlag(false);
                } else {
                    setAnswerFlag(true);
                }
            });
        }
    }, [userAnswers]);

    //useRef
    const departureRef = useRef();
    const destinationRef = useRef();
    const rangeDatePickerRef = useRef();

    //variable for selected city
    const [selectedDeparture, setSelectedDeparture] = useState(null);
    const [selectedDestination, setSelectedDestination] = useState(null);

    //request get city list
    //departure
    const [departureCities, setDepartureCities] = useState([]);
    const [departureCitiesCopy, setDepartureCitiesCopy] = useState([]);
    const [departureCitiesLoading, setDepartureCitiesLoading] = useState(false);
    const [departureSearchValue, setDepartureSearchValue] = useState("");
    const getDepartureCities = async input => {
        setDepartureCitiesLoading(true);
        const response = await axios.get(`${BASE_URL}/api/v2/basic/cities/`, {
            params: {
                search: input,
                all: true,
                accommodation: false,
                flight: true,
                has_plan: false,
            },
        });

        const departure = [...response.data.data.results];
        departure.sort((a, b) => b.usage_flight - a.usage_flight);
        const groupOptions = departure.map(option => {
            return {
                header: t("popularCities"),
                ...option,
            };
        });
        setDepartureCities(groupOptions);
        setDepartureCitiesCopy(groupOptions);
        setDepartureCitiesLoading(false);
    };

    //destination with pagination
    const [destinationCities, setDestinationCities] = useState([]);
    const [destinationCitiesCopy, setDestinationCitiesCopy] = useState([]);
    const [destinationCitiesLoading, setDestinationCitiesLoading] = useState(false);
    const [destinationSearchValue, setDestinationSearchValue] = useState("");
    const [destinationPage, setDestinationPage] = useState(1);
    const [destinationTotalPages, setDestinationTotalPages] = useState(1);

    const getDestinationCities = async (input = "", page = 1) => {
        setDestinationCitiesLoading(true);
        const response = await axios.get(`${BASE_URL}/api/v2/basic/cities/`, {
            params: {
                search: input,
                page,
                all: true,
                accommodation: false,
                flight: true,
                has_plan: true,
            },
        });

        const { results, current_page, total_pages } = response.data.data;
        const sorted = results.sort((a, b) => b.usage_flight - a.usage_flight).sort((a, b) => b.has_plan - a.has_plan);
        const groupOptions = sorted.map(option => ({
            header: option.has_plan ? t("itineraryCities") : t("popularCities"),
            ...option,
        }));

        if (page === 1) {
            setDestinationCities(groupOptions);
            setDestinationCitiesCopy(groupOptions);
        } else {
            setDestinationCities(prev => [...prev, ...groupOptions]);
        }

        setDestinationPage(current_page);
        setDestinationTotalPages(total_pages);
        setDestinationCitiesLoading(false);
    };

    useEffect(() => {
        getDepartureCities();
        getDestinationCities();
    }, []);

    //handle departure city change
    const onDepartureInputChange = useDebouncedInputChange(input => {
        getDepartureCities(input);
    }, 1000);

    const onDestinationInputValue = useDebouncedInputChange(input => {
        setDestinationPage(1);
        getDestinationCities(input, 1);
        setDestinationSearchValue(input);
    }, 1000);

    //remove city selected
    useEffect(() => {
        setDepartureCities(departureCitiesCopy?.filter(city => city?.id !== selectedDestination?.id));
        setDestinationCities(destinationCitiesCopy?.filter(city => city?.id !== selectedDeparture?.id));
    }, [selectedDeparture, selectedDestination]);

    //handle departure change
    const onDepartureChange = value => {
        setSelectedDeparture(value);
        if (IS_MOBILE) setShowAutocomplete({ departure: false, destination: selectedDestination || withoutDestination ? false : true });
        if (!selectedDestination) destinationRef?.current?.focus();
    };

    //handle destination change
    const onDestinationChange = value => {
        setSelectedDestination(value);
        if (IS_MOBILE) setShowAutocomplete({ departure: false, destination: false });
        if (!startDate && !endDate) rangeDatePickerRef.current.focus();
    };

    //handle without destination
    const [withoutDestination, setWithoutDestination] = useState(false);
    const [withoutDate, setWithoutDate] = useState(false);

    //handle date change
    const { startDate, endDate, defaultValueDate, handleDateFormatChange } = useDateChange(null, null, true);
    const [allDates, setAllDates] = useState(null);
    const [allPersonVisibility, setAllPersonVisibility] = useState(false);
    const dateChange = (dates, dateList, isPersianCalendar) => {
        // if (dates) {
        setAllDates(dateList);
        handleDateFormatChange(dates, isPersianCalendar);
        setAllPersonVisibility(true);
        // }
    };

    //handle person count
    const [personCounter, setPersonCounter] = useState({});
    //list of person types with their details
    const personList = [
        { id: 1, label: t("adult"), description: t("ageGroup1"), name: "adult", disabled: 1, validation: true },
        { id: 2, label: t("child"), description: t("ageGroup2"), name: "child", disabled: 0, validation: true },
        { id: 3, label: t("infant"), description: t("ageGroup3"), name: "infant", disabled: 0, validation: true },
    ];

    //User requests for tickets and hotels
    const [isRequiredTicket, setIsRequiredTicket] = useState(true);
    const handleIsRequiredTicket = event => {
        const checked = event.target.checked;
        setIsRequiredTicket(checked);
    };

    const [isRequiredHotel, setIsRequiredHotel] = useState(true);
    const handleIsRequiredHotel = event => {
        const checked = event.target.checked;
        setIsRequiredHotel(checked);
    };

    //remove check for the flight check box, if the selected city doesn't have airport
    useEffect(() => {
        if (selectedDeparture || selectedDestination) {
            if ((selectedDeparture?.flight && selectedDestination?.flight) == false) {
                setIsRequiredTicket(false);
            }
        }
    }, [selectedDestination, selectedDeparture]);

    //remove check for the hotel check box, if the selected city doesn't have hotel
    useEffect(() => {
        if (selectedDestination) {
            if (selectedDestination?.accommodation == false) {
                setIsRequiredHotel(false);
            }
        }
    }, [selectedDestination]);

    //category data
    const domesticFlightInformation = {
        departure: selectedDeparture,
        destination: withoutDestination ? null : selectedDestination,
        departureDate: withoutDate ? null : startDate,
        returnDate: withoutDate ? null : endDate,
        personCounter: personCounter,
    };
    const intFlightInformation = {
        departure: selectedDeparture,
        destination: withoutDestination ? null : selectedDestination,
        departureDate: withoutDate ? null : startDate,
        returnDate: withoutDate ? null : endDate,
        personCounter: personCounter,
        cabinType: CABIN_TYPE?.[0],
        isDirect: false,
    };
    const hotelInformation = {
        destination: withoutDestination ? null : selectedDestination,
        checkIn: withoutDate ? null : startDate,
        checkOut: withoutDate ? null : endDate,
        // personCounter: personCounter,
    };
    const itineraryInformation = {
        departure: selectedDeparture,
        destination: withoutDestination ? null : selectedDestination,
        departureDate: withoutDate ? null : startDate,
        returnDate: withoutDate ? null : endDate,
        personCounter: personCounter,
        itineraryGenerate: true,
        firstDayTime: accessToken
            ? userAnswers?.sleep_class === 1
                ? "08:00:00"
                : userAnswers?.sleep_class === 2
                  ? "09:00:00"
                  : userAnswers?.sleep_class === 3
                    ? "10:00:00"
                    : "09:00:00"
            : "09:00:00",
        lastDayTime: accessToken
            ? userAnswers?.sleep_class === 1
                ? "21:00:00"
                : userAnswers?.sleep_class === 2
                  ? "22:00:00"
                  : userAnswers?.sleep_class === 3
                    ? "23:00:00"
                    : "23:00:00"
            : "23:00:00",
        startTime: accessToken
            ? userAnswers?.sleep_class === 1
                ? "08:00:00"
                : userAnswers?.sleep_class === 2
                  ? "09:00:00"
                  : userAnswers?.sleep_class === 3
                    ? "10:00:00"
                    : "09:00:00"
            : "09:00:00",
        endTime: accessToken
            ? userAnswers?.sleep_class === 1
                ? "21:00:00"
                : userAnswers?.sleep_class === 2
                  ? "22:00:00"
                  : userAnswers?.sleep_class === 3
                    ? "23:00:00"
                    : "23:00:00"
            : "23:00:00",
    };
    const generalInformation = {
        isTicket: isRequiredTicket,
        isAccommodation: isRequiredHotel,
        isItinerary: true,
        isHotelInternational: selectedDestination?.country.id != "1" && !!selectedDestination ? true : false,
        isFlightInternational:
            selectedDeparture?.country.id != "1" || (selectedDestination?.country.id != "1" && !!selectedDestination) ? true : false,
        isAutomaticReserve: false,
    };

    //redirect to itinerary & save trip info
    const submitTripInformation = event => {
        event.preventDefault();

        if (!selectedDeparture) {
            departureRef?.current?.focus();
            return toast.error(t("errorMessage4"), { toastId: "custom-id-yes" });
        }

        if (!selectedDestination && !withoutDestination) {
            destinationRef?.current?.focus();
            return toast.error(t("errorMessage5"), { toastId: "custom-id-yes" });
        }

        if (!selectedDestination?.has_plan && !withoutDestination) {
            return toast.warning(t("errorMessage6"), { toastId: "custom-id-yes" });
        }

        if ((!startDate || !endDate) && !withoutDate) {
            rangeDatePickerRef?.current?.focus();
            return toast.error(t("errorMessage2"), { toastId: "custom-id-yes" });
        }

        if ((allDates?.length > 10 || allDates?.length === 1) && !withoutDate) {
            return toast.error(t("errorMessage3"), { toastId: "custom-id-yes" });
        }
        sessionStorage.setItem("domesticFlightInformation", JSON.stringify(domesticFlightInformation));
        sessionStorage.setItem("intFlightInformation", JSON.stringify(intFlightInformation));
        sessionStorage.setItem("hotelInformation", JSON.stringify(hotelInformation));
        sessionStorage.setItem("itineraryInformation", JSON.stringify(itineraryInformation));
        sessionStorage.setItem("generalInformation", JSON.stringify(generalInformation));

        const { isFlightInternational } = JSON.parse(sessionStorage.getItem("generalInformation")) || {};

        if (answerFlag) {
            sessionStorage.setItem("favorites", JSON.stringify(userAnswers));
            if (isRequiredTicket && !isFlightInternational ) {
                setRoutes([...routes, `/${locale}/itinerary/domestic-flight`]);
                sessionStorage.setItem(
                    "visitedRoutes",
                    JSON.stringify([...routes, `/${locale}/itinerary/favorites`, `/${locale}/itinerary/domestic-flight`]),
                );
                navigate(`/${locale}/itinerary/domestic-flight`);
            }
            if (isRequiredTicket && !!isFlightInternational) {
                setRoutes([...routes, `/${locale}/itinerary/international-flight`]);
                sessionStorage.setItem(
                    "visitedRoutes",
                    JSON.stringify([...routes, `/${locale}/itinerary/favorites`, `/${locale}/itinerary/international-flight`]),
                );
                navigate(`/${locale}/itinerary/international-flight`);
            }
            if (!isRequiredTicket && isRequiredHotel) {
                sessionStorage.removeItem("selectedFlight");
                setRoutes([...routes, `/${locale}/itinerary/hotel`]);
                sessionStorage.setItem("visitedRoutes", JSON.stringify([...routes, `/${locale}/itinerary/favorites`, `/${locale}/itinerary/hotel`]));
                navigate(`/${locale}/itinerary/hotel`);
            }
            if (!isRequiredTicket && !isRequiredHotel) {
                sessionStorage.removeItem("selectedFlight");
                sessionStorage.removeItem("selectedHotel");
                sessionStorage.removeItem("selectedRoomsInfo");
                setRoutes([...routes, `/${locale}/itinerary/itinerary-page`]);
                sessionStorage.setItem(
                    "visitedRoutes",
                    JSON.stringify([...routes, `/${locale}/itinerary/favorites`, `/${locale}/itinerary/itinerary-page`]),
                );
                navigate(`/${locale}/itinerary/itinerary-page`);
            }
        } else {
            navigate(`/${locale}/itinerary/favorites`);
        }
    };

    //handle swap change
    const handleSwapChange = () => {
        setSelectedDestination(selectedDeparture);
        setSelectedDeparture(selectedDestination);
    };

    return (
        <Grid
            className="search-form"
            component="form"
            container
            item
            xs={12}
            rowGap={2.5}
            justifyContent="space-between"
            alignItems="center"
            onSubmit={submitTripInformation}
        >
            <Grid container item xs={12} rowGap={1} columnGap={{ xs: 0, md: 2 }} justifyContent={{ xs: "space-between", md: "flex-start" }}>
                <Grid className="search-form__checkbox" container item xs={5.8} md="auto">
                    <BaseCheckbox
                        label={t("dontDestination")}
                        checked={withoutDestination}
                        handleChange={event => setWithoutDestination(event.target.checked)}
                    />
                </Grid>
                <Grid className="search-form__checkbox" container item xs={5.8} md="auto">
                    <BaseCheckbox label={t("dontDate")} checked={withoutDate} handleChange={event => setWithoutDate(event.target.checked)} />
                </Grid>
                <Grid className="search-form__checkbox" container item xs={5.8} md="auto">
                    <BaseCheckbox
                        label={t("iWantTicket")}
                        checked={isRequiredTicket}
                        handleChange={handleIsRequiredTicket}
                        disabled={
                            (!!selectedDestination || !!selectedDeparture) && ((selectedDeparture?.flight && selectedDestination?.flight) == false)
                                ? true
                                : false
                        }
                    />
                </Grid>

                <Grid className="search-form__checkbox" container item xs={5.8} md="auto">
                    <BaseCheckbox
                        label={t("iWantAccommodation")}
                        checked={isRequiredHotel}
                        handleChange={handleIsRequiredHotel}
                        disabled={!!selectedDestination && (selectedDestination?.accommodation == false) ? true : false}
                    />
                </Grid>
            </Grid>

            <Grid className="search-form__select-city" container item md={4.5} justifyContent="space-between" alignItems="center">
                {IS_MOBILE && (
                    <Grid container item xs={5.8} md={5.4}>
                        <BaseTextField
                            label={t("departure")}
                            value={locale === "en" ? selectedDeparture?.english_name : selectedDeparture?.name}
                            onClick={() => handleShowAutocomplete("departure")}
                            radius="10px"
                            icon={<Icon name="DepartureIcon" width="0.8rem" />}
                            readOnly={true}
                        />
                    </Grid>
                )}
                {showAutocomplete?.departure && (
                    <Grid container item md={5.4} className="search-form__autocomplete" justifyContent="space-between" alignItems="center">
                        <Grid container item xs={IS_MOBILE ? 10.5 : 12}>
                            <AutoCompleteComponent
                                label={t("departure")}
                                options={departureCities}
                                name="departure"
                                defaultValue={selectedDeparture}
                                handleChange={onDepartureChange}
                                setInputValue={onDepartureInputChange}
                                innerRef={departureRef}
                                control={control}
                                loading={departureCitiesLoading}
                                disabled={false}
                                width={350}
                                icon={<Icon name="DepartureIcon" width="0.8rem" />}
                                open={IS_MOBILE ? true : false}
                            />
                        </Grid>
                        {IS_MOBILE && (
                            <Grid container item xs={1}>
                                <CloseCircle
                                    color="var(--dark-color-60)"
                                    onClick={() => setShowAutocomplete({ departure: false, destination: false })}
                                />
                            </Grid>
                        )}
                    </Grid>
                )}
                <Box className="search-form__swap">
                    <ArrowSwapHorizontal
                        size={IS_MOBILE ? "18" : "24"}
                        color="var(--light-color)"
                        onClick={selectedDeparture && selectedDestination && handleSwapChange}
                    />
                </Box>

                {IS_MOBILE && (
                    <Grid container item xs={5.8} md={5.4}>
                        <BaseTextField
                            label={t("destination")}
                            value={locale === "en" ? selectedDestination?.english_name : selectedDestination?.name}
                            onClick={() => {
                                if (!withoutDestination) {
                                    handleShowAutocomplete("destination");
                                }
                            }}
                            radius="10px"
                            icon={<Location size="20" color={withoutDestination ? "var(--gray-color)" : "var(--teal-color-18)"} />}
                            disabled={withoutDestination}
                            readOnly={true}
                        />
                    </Grid>
                )}
                {showAutocomplete?.destination && (
                    <Grid container item md={5.4} className="search-form__autocomplete" justifyContent="space-between" alignItems="center">
                        <Grid container item xs={IS_MOBILE ? 10.5 : 12}>
                            <AutoCompleteComponent
                                label={t("destination")}
                                options={destinationCities}
                                name="destination"
                                defaultValue={selectedDestination}
                                handleChange={onDestinationChange}
                                setInputValue={onDestinationInputValue}
                                innerRef={destinationRef}
                                control={control}
                                loading={destinationCitiesLoading}
                                disabled={withoutDestination}
                                width={350}
                                icon={<Location size="20" color="var(--teal-color-18)" />}
                                open={IS_MOBILE ? true : false}
                                hasMore={destinationPage < destinationTotalPages}
                                onLoadMore={() => {
                                    if (destinationPage < destinationTotalPages) {
                                        getDestinationCities(destinationSearchValue, destinationPage + 1);
                                    }
                                }}
                            />
                        </Grid>
                        {IS_MOBILE && (
                            <Grid container item xs={1}>
                                <CloseCircle
                                    color="var(--dark-color-60)"
                                    onClick={() => setShowAutocomplete({ departure: false, destination: false })}
                                />
                            </Grid>
                        )}
                    </Grid>
                )}
            </Grid>

            <Grid container item md={4.5}>
                <RangeDatePicker
                    handleChange={dateChange}
                    innerRef={rangeDatePickerRef}
                    labels={[t("departureDate"), t("returnDate")]}
                    minDate={new Date().setHours(0, 0, 0, 0)}
                    disableDate={withoutDate}
                    range={true}
                />
            </Grid>

            <Grid container item md={1.5}>
                <PersonCount list={personList} menuVisibility={allPersonVisibility} handleChange={setPersonCounter} />
            </Grid>

            <Grid container item md={1}>
                <BaseButton
                    text={`${t("search")}`}
                    color="primary-10"
                    size="large"
                    radius="radius-20"
                    type="submit"
                    leftIcon={<SearchNormal size="24" color="var(--primary-color)" />}
                />
            </Grid>
        </Grid>
    );
}
